import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { Loader2, CheckCircle2, Video, FileText, Download, Award, ChevronRight, PlayCircle, Lock, MessageSquare, Trash2, Send, HelpCircle, XCircle, Users, Calendar, Package } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import api, { formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";

/**
 * CoursePlayer — learner UI for the native LMS.
 *
 * • Left rail: ordered lesson list with completion checkmarks + progress bar.
 * • Main pane: renders active lesson (markdown / embedded video / download link).
 * • "Mark Complete" advances progress; on 100% a "Download Certificate" CTA appears.
 */
export default function CoursePlayer() {
  const { productId } = useParams();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [marking, setMarking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/lms/courses/${productId}`);
      setCourse(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [productId]);

  // Fire GA4 select_content when the active lesson changes
  useEffect(() => {
    if (!course || !course.lessons?.[activeIdx]) return;
    const l = course.lessons[activeIdx];
    track("select_content", {
      content_type: "lesson",
      item_id: l.id,
      item_name: l.title,
      course_id: course.product?.id,
      course_name: course.product?.name,
      lesson_index: activeIdx + 1,
    });
  }, [activeIdx, course]);

  const active = course?.lessons?.[activeIdx];
  const done = course && course.total_count > 0 && course.completed_count === course.total_count;

  const markComplete = async () => {
    if (!active) return;
    setMarking(true);
    try {
      await api.post(`/lms/courses/${productId}/complete`, { lesson_id: active.id });
      // Refresh course; auto-advance to next incomplete
      const { data } = await api.get(`/lms/courses/${productId}`);
      setCourse(data);
      const nextIdx = data.lessons.findIndex((l, i) => i > activeIdx && !l.completed);
      if (nextIdx !== -1) setActiveIdx(nextIdx);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setMarking(false);
    }
  };

  const downloadCert = async () => {
    try {
      const res = await api.get(`/lms/courses/${productId}/certificate`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `catalyst-certificate-${productId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  if (loading) {
    return (
      <SiteLayout branch="main">
        <div className="section text-center">
          <Loader2 className="animate-spin mx-auto text-[var(--navy)]" size={32} />
        </div>
      </SiteLayout>
    );
  }

  if (error || !course) {
    return (
      <SiteLayout branch="main">
        <div className="section max-w-2xl mx-auto text-center">
          <h1 className="font-display text-3xl text-[var(--navy)]">Course unavailable</h1>
          <p className="text-[var(--muted)] mt-4">{error || "This course does not exist yet."}</p>
          <Link to="/academy" className="btn btn-navy mt-6 inline-flex">Back to Academy</Link>
        </div>
      </SiteLayout>
    );
  }

  if (course.total_count === 0) {
    // If the admin uploaded a Rise / Articulate 360 zip (top-level "Upload Rise Zip"
    // on the product card), it lives on the product doc, not as LMS lessons.
    // Bridge the learner straight into the Rise viewer instead of leaving them stranded.
    if (course.product?.course_uploaded_at) {
      return (
        <SiteLayout branch="main">
          <div className="section max-w-2xl mx-auto text-center">
            <span className="eyebrow-dark inline-flex items-center gap-2"><PlayCircle size={14} /> RISE / ARTICULATE COURSE</span>
            <h1 className="font-display text-3xl text-[var(--navy)] mt-3">{course.product.name}</h1>
            <p className="text-[var(--muted)] mt-4">
              This course is a Rise 360 export ({course.product.course_file_count || "several"} files). Launch it fullscreen below.
            </p>
            <Link
              to={`/course/${productId}`}
              className="btn btn-navy mt-6 inline-flex"
              data-testid="launch-rise-fullscreen"
            >
              <PlayCircle size={14} className="mr-2" /> Launch Rise course
            </Link>
          </div>
        </SiteLayout>
      );
    }
    return (
      <SiteLayout branch="main">
        <div className="section max-w-2xl mx-auto text-center">
          <h1 className="font-display text-3xl text-[var(--navy)]">{course.product.name}</h1>
          <p className="text-[var(--muted)] mt-4">This course doesn't have any lessons yet — check back soon.</p>
          <Link to="/academy" className="btn btn-navy mt-6 inline-flex">Back to Academy</Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout branch="main">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-10">
        {course.cohort && (
          <div className="mb-6 flex items-center gap-3 bg-[var(--cream)] border border-[var(--gold)]/40 px-4 py-3" data-testid="cohort-banner">
            <Users size={16} className="text-[var(--gold)]" />
            <div className="text-xs">
              <span className="font-bold text-[var(--navy)] uppercase tracking-widest mr-2">Cohort:</span>
              <span className="text-[var(--navy)] font-bold">{course.cohort.name}</span>
              <span className="text-[var(--muted)] ml-3 inline-flex items-center gap-1">
                <Calendar size={12} /> Starts {new Date(course.cohort.start_date).toLocaleDateString()}
                {course.cohort.end_date && <> · Ends {new Date(course.cohort.end_date).toLocaleDateString()}</>}
              </span>
            </div>
          </div>
        )}
        <div className="grid md:grid-cols-[320px,1fr] gap-8">
          {/* Sidebar */}
          <aside className="md:sticky md:top-6 self-start" data-testid="course-sidebar">
            <span className="eyebrow-dark">COURSE</span>
            <h1 className="font-display text-2xl text-[var(--navy)] mt-3 leading-tight">{course.product.name}</h1>
            {/* Progress */}
            <div className="mt-5">
              <div className="flex items-center justify-between text-xs text-[var(--muted)] mb-1.5">
                <span className="font-bold tracking-wider uppercase">Progress</span>
                <span>{course.completed_count} / {course.total_count}</span>
              </div>
              <div className="h-1.5 bg-[var(--line)] overflow-hidden">
                <div
                  className="h-full bg-[var(--gold)] transition-all"
                  style={{ width: `${course.percent}%` }}
                  data-testid="course-progress-bar"
                />
              </div>
              <div className="text-[10px] text-[var(--muted)] mt-1 tracking-wider">{course.percent}% complete</div>
            </div>
            {/* Certificate */}
            {done && (
              <button
                onClick={downloadCert}
                className="btn btn-gold w-full justify-center mt-5 !text-[11px]"
                data-testid="download-certificate"
              >
                <Award size={13} className="mr-2" /> Download Certificate
              </button>
            )}
            {/* Lesson list */}
            <ol className="mt-6 space-y-1" data-testid="lesson-list">
              {course.lessons.map((l, i) => {
                const isActive = i === activeIdx;
                const Icon = l.content_type === "video" ? Video
                  : l.content_type === "download" ? Download
                  : l.content_type === "quiz" ? HelpCircle
                  : l.content_type === "scorm" ? Package
                  : FileText;
                return (
                  <li key={l.id}>
                    <button
                      onClick={() => setActiveIdx(i)}
                      className={`w-full text-left flex items-center gap-2.5 px-3 py-2.5 border-l-2 transition-all ${
                        isActive
                          ? "border-l-[var(--gold)] bg-white"
                          : "border-l-transparent hover:bg-white/50"
                      } ${!l.unlocked ? "opacity-60" : ""}`}
                      data-testid={`lesson-tab-${l.id}`}
                    >
                      {l.completed ? (
                        <CheckCircle2 size={14} className="text-green-700 flex-shrink-0" />
                      ) : !l.unlocked ? (
                        <Lock size={14} className="text-[var(--muted)] flex-shrink-0" />
                      ) : (
                        <Icon size={14} className="text-[var(--muted)] flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className={`text-xs font-bold truncate ${isActive ? "text-[var(--navy)]" : "text-[var(--ink)]"}`}>
                          {i + 1}. {l.title}
                        </div>
                        <div className="text-[10px] text-[var(--muted)] tracking-wider uppercase">
                          {!l.unlocked && l.unlock_at
                            ? `Unlocks ${new Date(l.unlock_at).toLocaleDateString()}`
                            : `${l.content_type} ${l.est_minutes ? `· ${l.est_minutes} min` : ""}`}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ol>
          </aside>

          {/* Main content */}
          <section className="bg-white border border-[var(--line)] p-8 md:p-10 min-h-[500px]" data-testid="lesson-content-pane">
            <div className="flex items-center justify-between mb-6">
              <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--gold)]">
                Lesson {activeIdx + 1} of {course.total_count}
              </span>
              {active?.completed && (
                <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-2 py-1 text-[10px] font-bold tracking-widest">
                  <CheckCircle2 size={10} /> COMPLETED
                </span>
              )}
            </div>
            <h2 className="font-display text-3xl md:text-4xl text-[var(--navy)] leading-tight">{active.title}</h2>

            <div className="mt-8">
              {!active.unlocked ? (
                <div className="bg-[var(--cream)] border border-[var(--line)] p-8 text-center" data-testid="lesson-locked">
                  <Lock className="mx-auto text-[var(--muted)]" size={32} />
                  <h3 className="font-display text-xl text-[var(--navy)] mt-4">This lesson is locked</h3>
                  <p className="text-sm text-[var(--muted)] mt-2">
                    {active.unlock_at
                      ? <>Unlocks on <strong className="text-[var(--navy)]">{new Date(active.unlock_at).toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</strong>.</>
                      : <>Complete an earlier lesson first to start the drip schedule.</>}
                  </p>
                </div>
              ) : (
                <>
                  {active.content_type === "video" && active.video_url && (
                    <VideoEmbed url={active.video_url} captionsUrl={active.captions_url} />
                  )}
                  {active.content_type === "markdown" && active.content && (
                    <div className="prose prose-neutral max-w-none text-[15px] leading-relaxed" data-testid="lesson-markdown">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw, rehypeSanitize]}>{active.content}</ReactMarkdown>
                    </div>
                  )}
                  {active.content_type === "download" && active.download_url && (
                    <a
                      href={active.download_url}
                      target="_blank" rel="noreferrer"
                      className="btn btn-navy inline-flex" data-testid="lesson-download-link"
                    >
                      <Download size={14} className="mr-2" /> Download resource
                    </a>
                  )}
                  {active.content_type === "quiz" && (
                    <QuizPlayer
                      lesson={active}
                      onPassed={() => load()}
                    />
                  )}
                  {active.content_type === "scorm" && (
                    <ScormPlayer lesson={active} onProgress={() => load()} />
                  )}
                  {!active.content && !active.video_url && !active.download_url && !active.quiz && active.content_type !== "scorm" && (
                    <p className="text-[var(--muted)] italic">This lesson's content is being finalized.</p>
                  )}
                </>
              )}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-[var(--line)] pt-6">
              {active.unlocked && !active.completed && active.content_type !== "quiz" && active.content_type !== "scorm" && (
                <button
                  onClick={markComplete}
                  disabled={marking}
                  className="btn btn-navy disabled:opacity-60"
                  data-testid="mark-complete"
                >
                  {marking ? <><Loader2 className="animate-spin mr-2" size={14} /> Saving…</> : <><CheckCircle2 size={14} className="mr-2" /> Mark Complete</>}
                </button>
              )}
              {active.unlocked && active.completed && (
                <span className="text-sm text-[var(--muted)]">You've completed this lesson.</span>
              )}
              {active.unlocked && active.content_type === "markdown" && active.content && (
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/lms/lessons/${active.id}/transcript`}
                  target="_blank" rel="noreferrer"
                  className="btn btn-ghost"
                  data-testid="lesson-transcript"
                >
                  <FileText size={14} className="mr-1" /> Download transcript
                </a>
              )}
              {activeIdx < course.total_count - 1 && (
                <button
                  onClick={() => setActiveIdx(activeIdx + 1)}
                  className="btn btn-ghost"
                  data-testid="next-lesson"
                >
                  Next lesson <ChevronRight size={14} className="ml-1" />
                </button>
              )}
            </div>

            {/* Discussion */}
            {active.unlocked && (
              <LessonComments lessonId={active.id} currentUser={user} />
            )}
          </section>
        </div>
      </div>
    </SiteLayout>
  );
}

function VideoEmbed({ url, captionsUrl }) {
  const embed = useMemo(() => toEmbedUrl(url), [url]);
  if (embed.type === "iframe") {
    return (
      <div className="aspect-video w-full bg-black" data-testid="lesson-video">
        <iframe
          src={embed.src}
          title="Lesson video"
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <video controls className="w-full aspect-video bg-black" data-testid="lesson-video" crossOrigin="anonymous">
      <source src={url} />
      {captionsUrl && (
        <track kind="captions" srcLang="en" label="English" src={captionsUrl} default />
      )}
      Your browser doesn't support HTML5 video.
    </video>
  );
}

function toEmbedUrl(url) {
  try {
    const u = new URL(url);
    const host = u.hostname.replace("www.", "");
    if (host === "youtube.com" || host === "m.youtube.com") {
      const id = u.searchParams.get("v");
      if (id) return { type: "iframe", src: `https://www.youtube.com/embed/${id}` };
    }
    if (host === "youtu.be") {
      const id = u.pathname.replace(/^\//, "");
      if (id) return { type: "iframe", src: `https://www.youtube.com/embed/${id}` };
    }
    if (host.endsWith("vimeo.com")) {
      const id = u.pathname.replace(/^\//, "");
      if (id) return { type: "iframe", src: `https://player.vimeo.com/video/${id}` };
    }
    if (host.includes("loom.com") && u.pathname.startsWith("/share/")) {
      return { type: "iframe", src: url.replace("/share/", "/embed/") };
    }
  } catch { /* fall through to native player */ }
  return { type: "native" };
}


/* -------------------- QuizPlayer -------------------- */
function QuizPlayer({ lesson, onPassed }) {
  const quiz = lesson.quiz || { questions: [], pass_threshold: 0.8 };
  const [answers, setAnswers] = useState(Array(quiz.questions.length).fill(null));
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (answers.some((a) => a === null)) {
      setError("Please answer every question before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/lms/lessons/${lesson.id}/quiz-attempt`, { answers });
      setResult(data);
      if (data.passed && onPassed) onPassed();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const retry = () => {
    setResult(null);
    setAnswers(Array(quiz.questions.length).fill(null));
  };

  if (!quiz.questions.length) {
    return <p className="text-[var(--muted)] italic">This quiz has no questions yet.</p>;
  }

  return (
    <div data-testid="quiz-player">
      <div className="mb-6 bg-[var(--cream)] border-l-4 border-[var(--gold)] p-4">
        <div className="text-[10px] tracking-widest text-[var(--gold)] font-bold flex items-center gap-2">
          <HelpCircle size={12} /> QUIZ · {Math.round((quiz.pass_threshold || 0.8) * 100)}% TO PASS
        </div>
        <p className="text-sm text-[var(--ink)] mt-1">Answer all questions to submit.</p>
      </div>

      <div className="space-y-8">
        {quiz.questions.map((q, qi) => {
          const perQ = result?.per_question?.[qi];
          return (
            <div key={qi} data-testid={`quiz-q-${qi}`}>
              <div className="font-bold text-[var(--navy)] text-base mb-3">
                {qi + 1}. {q.q}
              </div>
              <div className="space-y-2">
                {(q.options || []).map((opt, oi) => {
                  const chosen = answers[qi] === oi;
                  const isCorrect = perQ && perQ.correct === oi;
                  const isWrong = perQ && perQ.your_answer === oi && !perQ.is_correct;
                  const disabled = !!result;
                  return (
                    <label
                      key={oi}
                      className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${
                        disabled
                          ? isCorrect
                            ? "bg-green-50 border-green-500"
                            : isWrong
                            ? "bg-red-50 border-red-400"
                            : "border-[var(--line)] opacity-60"
                          : chosen
                          ? "border-[var(--navy)] bg-white"
                          : "border-[var(--line)] hover:border-[var(--navy)]"
                      }`}
                    >
                      <input
                        type="radio"
                        name={`q-${qi}`}
                        checked={chosen}
                        disabled={disabled}
                        onChange={() => {
                          const next = [...answers];
                          next[qi] = oi;
                          setAnswers(next);
                        }}
                        data-testid={`quiz-q-${qi}-opt-${oi}`}
                      />
                      <span className="text-sm text-[var(--ink)] flex-1">{opt}</span>
                      {disabled && isCorrect && <CheckCircle2 size={16} className="text-green-700" />}
                      {disabled && isWrong && <XCircle size={16} className="text-red-600" />}
                    </label>
                  );
                })}
              </div>
              {result && perQ?.explanation && (
                <div className="mt-2 text-xs text-[var(--muted)] italic">↳ {perQ.explanation}</div>
              )}
            </div>
          );
        })}
      </div>

      {error && <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 p-3">{error}</div>}

      {!result ? (
        <button
          onClick={submit}
          disabled={submitting}
          className="btn btn-navy mt-8 disabled:opacity-60"
          data-testid="quiz-submit"
        >
          {submitting ? <><Loader2 className="animate-spin mr-2" size={14} /> Grading…</> : "Submit Quiz"}
        </button>
      ) : (
        <div className="mt-8 border-t border-[var(--line)] pt-6" data-testid="quiz-result">
          <div className={`inline-flex items-center gap-2 px-4 py-2 text-xs font-bold tracking-widest ${
            result.passed ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
          }`}>
            {result.passed ? <><CheckCircle2 size={14} /> PASSED</> : <><XCircle size={14} /> DID NOT PASS</>}
          </div>
          <div className="font-display text-2xl text-[var(--navy)] mt-3">
            {result.correct} / {result.total} · {Math.round(result.score * 100)}%
          </div>
          {!result.passed && (
            <button onClick={retry} className="btn btn-ghost mt-4" data-testid="quiz-retry">Try again</button>
          )}
        </div>
      )}
    </div>
  );
}

/* -------------------- ScormPlayer -------------------- */
function ScormPlayer({ lesson, onProgress }) {
  const iframeRef = useRef(null);
  const cmiRef = useRef({});
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [entryRes, stateRes] = await Promise.all([
          api.get(`/lms/scorm/${lesson.id}/entry`),
          api.get(`/lms/scorm/${lesson.id}/state`),
        ]);
        setMeta(entryRes.data);
        cmiRef.current = stateRes.data || {};
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, [lesson.id]);

  // Expose SCORM 1.2 API on window for the iframe SCO to discover via parent.API
  useEffect(() => {
    if (!meta) return;
    const cmi = cmiRef.current;
    const flatten = (obj, prefix = "") => {
      const out = {};
      for (const [k, v] of Object.entries(obj || {})) {
        const key = prefix ? `${prefix}.${k}` : k;
        if (v && typeof v === "object" && !Array.isArray(v)) Object.assign(out, flatten(v, key));
        else out[key] = v;
      }
      return out;
    };
    const inflate = (flat) => {
      const out = {};
      for (const [k, v] of Object.entries(flat)) {
        const parts = k.split(".");
        let cur = out;
        while (parts.length > 1) {
          const p = parts.shift();
          cur[p] = cur[p] || {};
          cur = cur[p];
        }
        cur[parts[0]] = v;
      }
      return out;
    };
    const store = flatten(cmi);
    let errorCode = "0";
    const persist = async () => {
      try {
        await api.post(`/lms/scorm/${lesson.id}/state`, { cmi: inflate(store) });
        if (onProgress) onProgress();
      } catch (e) {
        errorCode = "101"; // General error
      }
    };
    window.API = {
      LMSInitialize: () => { errorCode = "0"; return "true"; },
      LMSFinish: () => { persist(); return "true"; },
      LMSCommit: () => { persist(); return "true"; },
      LMSGetValue: (k) => store[k] ?? "",
      LMSSetValue: (k, v) => { store[k] = String(v); errorCode = "0"; return "true"; },
      LMSGetLastError: () => errorCode,
      LMSGetErrorString: () => "",
      LMSGetDiagnostic: () => "",
    };
    setReady(true);
    return () => { try { delete window.API; } catch { /* noop */ } };
  }, [meta, lesson.id, onProgress]);

  if (error) return <div className="text-sm text-red-700 bg-red-50 border border-red-200 p-4">{error}</div>;
  if (!meta || !ready) return <div className="text-[var(--muted)] italic">Loading SCORM package…</div>;

  const src = `${backendUrl}/api/lms/scorm/${lesson.id}/asset/${meta.entry}`;
  return (
    <div className="aspect-video w-full bg-black border border-[var(--line)]" data-testid="scorm-player">
      <iframe
        ref={iframeRef}
        src={src}
        title={meta.title || lesson.title}
        className="w-full h-full"
        allow="fullscreen; autoplay"
      />
    </div>
  );
}

/* -------------------- LessonComments -------------------- */
function LessonComments({ lessonId, currentUser }) {
  const [items, setItems] = useState([]);
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/lms/lessons/${lessonId}/comments`);
      setItems(data);
    } catch (e) {
      // silent — comments are non-critical
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [lessonId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setPosting(true);
    setError("");
    try {
      await api.post(`/lms/lessons/${lessonId}/comments`, { body: body.trim() });
      setBody("");
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setPosting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/lms/comments/${id}`);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const canDelete = (c) => currentUser && (c.user_email === currentUser.email || currentUser.role === "admin");

  return (
    <div className="mt-10 border-t border-[var(--line)] pt-6" data-testid="lesson-comments">
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare size={14} className="text-[var(--gold)]" />
        <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--navy)]">
          Discussion · {items.length}
        </span>
      </div>

      <form onSubmit={submit} className="mb-6">
        <textarea
          rows={2}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Share a question or insight with fellow learners…"
          className="w-full px-3 py-2 border border-[var(--line)] bg-[#fafafa] text-sm focus:bg-white focus:border-[var(--navy)] focus:outline-none"
          data-testid="comment-body"
        />
        <div className="flex justify-between items-center mt-2">
          {error && <span className="text-xs text-red-700">{error}</span>}
          <button
            type="submit"
            disabled={posting || !body.trim()}
            className="btn btn-navy !py-1.5 !px-3 !text-[11px] ml-auto disabled:opacity-60"
            data-testid="comment-submit"
          >
            {posting ? <><Loader2 className="animate-spin mr-1" size={12} /> Posting…</> : <><Send size={12} className="mr-1" /> Post</>}
          </button>
        </div>
      </form>

      {items.length === 0 && (
        <p className="text-sm text-[var(--muted)] italic">No comments yet — be the first to share.</p>
      )}

      <ul className="space-y-4">
        {items.map((c) => (
          <li key={c.id} className="border-l-2 border-[var(--gold)] pl-4" data-testid={`comment-${c.id}`}>
            <div className="flex items-center gap-2 text-[11px] text-[var(--muted)] mb-1">
              <span className="font-bold text-[var(--navy)]">{c.user_name || c.user_email}</span>
              <span>· {new Date(c.created_at).toLocaleDateString()}</span>
              {canDelete(c) && (
                <button
                  onClick={() => remove(c.id)}
                  className="ml-auto text-red-700 hover:text-red-900"
                  data-testid={`comment-delete-${c.id}`}
                  title="Delete"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
            <div className="text-sm text-[var(--ink)] whitespace-pre-wrap">{c.body}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}
